import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import serviceApi from '../services/serviceApi';
import { REQUEST_STATUS_LABELS } from '../config/serviceConstants';
import { formatDate, formatDateWithDay, formatRelativeTime, formatTime } from '../utils/dateFormatter';
import ClientLayout from '../components/ClientLayout';
import '../styles/ClientLayout.css';
import { WHATSAPP_NUMBER } from '../config/clientPortalConstants';
import '../styles/ClientDashboard.css';

/**
 * Client Dashboard — uses ClientLayout wrapper, NO AuthContext.
 * Accessed via /client/t/:token (public route, token-based auth).
 * See CLEANING_SERVICE_ARCHITECTURE.md → Client Portal Architecture Decisions.
 */

const TIME_SLOTS = [
  { value: '10:00', label: '١٠:٠٠ ص' },
  { value: '13:00', label: '٠١:٠٠ ظ' },
  { value: '16:00', label: '٠٤:٠٠ م' },
];

const getTomorrow = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
};


const STATUS_ICONS = {
  pending: 'bi-hourglass-split',
  scheduled: 'bi-calendar-check',
  'in-progress': 'bi-arrow-repeat',
  completed: 'bi-check-circle-fill',
  cancelled: 'bi-x-circle',
};

const ClientDashboard = () => {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Bottom sheet
  const [sheetOpen, setSheetOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  // Cancel request
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  // Edit name
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [editNameSaving, setEditNameSaving] = useState(false);

  // Request form
  const [form, setForm] = useState({
    locationId: '',
    requestedDate: getTomorrow(),
    requestedTimeSlot: '',
    notes: '',
  });

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await serviceApi.clientPortal.getDashboard(token);
      const d = res.data || {};
      setData(d);

      if (d.locations?.length === 1) {
        setForm(prev => ({ ...prev, locationId: d.locations[0]._id }));
      }
    } catch (err) {
      console.error('Client dashboard error:', err);
      const status = err.response?.status;
      if (status === 401) {
        setError('invalid_token');
      } else {
        setError('network');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const totalBalance = data?.subscriptions?.reduce((sum, sub) => {
    return sum + (sub.effectiveBalance ?? (sub.visitsIncluded - sub.visitsUsed));
  }, 0) ?? 0;

  const totalIncluded = data?.subscriptions?.reduce((sum, sub) => sum + sub.visitsIncluded, 0) ?? 0;
  const balancePct = totalIncluded > 0 ? Math.max(0, (totalBalance / totalIncluded) * 100) : 0;

  // Earliest subscription end date — gives client context on when balance resets
  const earliestCycleEnd = data?.subscriptions?.reduce((earliest, sub) => {
    if (!sub.billingCycleEnd) return earliest;
    return !earliest || new Date(sub.billingCycleEnd) < new Date(earliest) ? sub.billingCycleEnd : earliest;
  }, null);

  const getBalanceLevel = () => {
    if (totalBalance <= 0) return 'zero';
    if (totalBalance <= 2) return 'low';
    return 'ok';
  };

  const handleFormChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleOpenSheet = () => {
    setSubmitError(null);
    setConfirmation(null);
    setForm(prev => ({
      ...prev,
      requestedDate: getTomorrow(),
      requestedTimeSlot: '',
      notes: '',
    }));
    setSheetOpen(true);
  };

  const handleSubmit = async () => {
    setSubmitError(null);

    if (!form.locationId) {
      setSubmitError('يرجى اختيار الموقع');
      return;
    }
    if (!form.requestedDate) {
      setSubmitError('يرجى اختيار التاريخ');
      return;
    }

    setSubmitting(true);
    try {
      const res = await serviceApi.clientPortal.submitRequest(token, {
        locationId: form.locationId,
        requestedDate: form.requestedDate,
        requestedTimeSlot: (form.requestedTimeSlot && form.requestedTimeSlot !== 'custom') ? form.requestedTimeSlot : undefined,
        notes: form.notes || undefined,
      });

      const submittedRequest = res.data?.data || res.data || {};
      const task = submittedRequest.task || {};
      const locationName = (data?.locations || []).find(l => l._id === form.locationId)?.name || '—';
      setConfirmation({
        ticketId: task.ticketId,
        requestedDate: form.requestedDate,
        locationName,
      });

      fetchDashboard();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'حدث خطأ — يرجى المحاولة لاحقاً';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!cancelConfirm) return;
    setCancelling(true);
    try {
      await serviceApi.clientPortal.cancelRequest(token, cancelConfirm._id);
      setCancelling(false);
      setCancelConfirm(null);
      setToast('تم إلغاء الطلب بنجاح');
      setTimeout(() => setToast(null), 3000);
      fetchDashboard();
    } catch (err) {
      setCancelling(false);
      const msg = err.response?.data?.error || 'حدث خطأ — يرجى المحاولة لاحقاً';
      setCancelConfirm(null);
      setToast(msg);
      setTimeout(() => setToast(null), 4000);
    }
  };

  const handleOpenEditName = () => {
    setEditNameValue(data?.clientName || '');
    setEditNameOpen(true);
  };

  const handleSaveName = async () => {
    const trimmed = editNameValue.trim();
    if (!trimmed || trimmed.length < 2) return;
    setEditNameSaving(true);
    try {
      await serviceApi.clientPortal.updateProfile(token, { name: trimmed });
      setEditNameOpen(false);
      setToast('تم تحديث الاسم');
      setTimeout(() => setToast(null), 3000);
      fetchDashboard();
    } catch (err) {
      const msg = err.response?.data?.error || 'حدث خطأ';
      setToast(msg);
      setTimeout(() => setToast(null), 4000);
    } finally {
      setEditNameSaving(false);
    }
  };

  const activeRequests = data?.activeRequests || [];
  const pastRequests = data?.pastRequests || [];

  // ── Loading ──
  if (loading) {
    return (
      <div className="cd-layout-loading">
        <div className="cd-layout-loading__spinner" />
        <span className="cd-layout-loading__text">جاري التحميل...</span>
      </div>
    );
  }

  // ── Error: invalid token ──
  if (error === 'invalid_token') {
    return (
      <div className="cd-layout-error">
        <div className="cd-layout-error__icon"><i className="bi bi-shield-x" /></div>
        <div className="cd-layout-error__title">رابط غير صالح</div>
        <div className="cd-layout-error__sub">
          هذا الرابط لم يعد صالحاً أو غير صحيح.
          <br />
          تواصل مع فريق الدعم للحصول على رابط جديد.
        </div>
        <a
          href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('مرحباً، أحتاج رابط جديد للوحة خدمات النظافة')}`}
          className="cd-layout-error__wa"
        >
          <i className="bi bi-whatsapp" />
          تواصل عبر واتساب
        </a>
      </div>
    );
  }

  // ── Error: network ──
  if (error === 'network') {
    return (
      <div className="cd-layout-error">
        <div className="cd-layout-error__icon"><i className="bi bi-wifi-off" /></div>
        <div className="cd-layout-error__title">تعذر الاتصال</div>
        <div className="cd-layout-error__sub">تحقق من اتصال الإنترنت وحاول مرة أخرى</div>
        <button onClick={fetchDashboard} className="cd-layout-error__retry">
          <i className="bi bi-arrow-clockwise" style={{ marginInlineStart: 6 }} />
          إعادة المحاولة
        </button>
      </div>
    );
  }

  const balanceLevel = getBalanceLevel();

  /** Render a single request card (used for both active and past) */
  const renderRequestCard = (req) => {
    const hasSchedule = !!req.taskId?.scheduledDate;
    const scheduledTime = req.taskId?.scheduledTime ? formatTime(req.taskId.scheduledTime) : null;
    // On pending cards, show the client's requested time slot if no scheduled time yet
    const requestedTime = req.requestedTimeSlot ? formatTime(req.requestedTimeSlot) : null;
    const displayTime = scheduledTime || requestedTime;

    // Primary date: scheduled if available, otherwise requested
    const primaryDate = hasSchedule ? req.taskId.scheduledDate : req.requestedDate;
    const isPending = req.status === 'pending';

    return (
    <div key={req._id} className={`cd-req-card cd-req-card--${req.status}`}>
      <div className="cd-req-card__top">
        <div className="cd-req-card__main">
          {isPending && (
            <span className="cd-req-card__date-label">التاريخ المطلوب</span>
          )}
          <span className="cd-req-card__date">{formatDateWithDay(primaryDate)}</span>
          {displayTime && (
            <span className="cd-req-card__time">
              <i className="bi bi-clock" />
              {displayTime}
            </span>
          )}
          <span className="cd-req-card__location">
            <i className="bi bi-geo-alt" />
            {req.locationId?.name || '—'}
          </span>
        </div>
        <span className={`cd-status cd-status--${req.status}`}>
          <i className={`bi ${STATUS_ICONS[req.status] || 'bi-circle'}`} />
          {REQUEST_STATUS_LABELS[req.status] || req.status}
        </span>
      </div>
      <div className="cd-req-card__bottom">
        {req.taskId?.ticketId && (
          <span className="cd-req-card__number">{req.taskId.ticketId}</span>
        )}
        {req.updatedAt && (
          <span className="cd-req-card__updated">
            آخر تحديث: {formatRelativeTime(req.updatedAt)}
          </span>
        )}
        {['pending', 'scheduled'].includes(req.status) && (
          <button
            className="cd-req-card__cancel"
            onClick={() => setCancelConfirm(req)}
            type="button"
          >
            <i className="bi bi-x-lg" />
            إلغاء
          </button>
        )}
      </div>
    </div>
  );
  };

  return (
    <ClientLayout activeTab="home" clientName={data?.clientName} clientEmail={data?.clientEmail} onEditName={handleOpenEditName}>
      <div className="cd-page">

      {/* Balance Card */}
      <div className="cd-balance">
        <div className="cd-balance__inner">
          <div className={`cd-balance__number cd-balance__number--${balanceLevel}`}>
            {totalBalance}
          </div>
          <div className="cd-balance__meta">
            <span className="cd-balance__label">زيارة متبقية</span>
            <span className="cd-balance__total">من أصل {totalIncluded.toLocaleString('ar-SA')}</span>
            {earliestCycleEnd && (
              <span className="cd-balance__cycle-end">حتى {formatDate(earliestCycleEnd)}</span>
            )}
          </div>
        </div>
        <div className="cd-balance__bar">
          <div
            className={`cd-balance__bar-fill cd-balance__bar-fill--${balanceLevel}`}
            style={{ width: `${balancePct}%` }}
          />
        </div>
      </div>

      {/* CTA */}
      <div className="cd-cta-area">
        <button
          className="cd-cta"
          onClick={handleOpenSheet}
          disabled={totalBalance <= 0}
        >
          <i className="bi bi-stars cd-cta__icon" />
          طلب خدمة نظافة
        </button>
        {totalBalance <= 0 && (
          <div className="cd-cta-blocked">
            لا يوجد رصيد — تواصل معنا لتجديد الاشتراك
          </div>
        )}
      </div>

      {/* Active Requests */}
      {activeRequests.length > 0 && (
        <div className="cd-requests">
          <div className="cd-requests__header">
            <span className="cd-requests__title">طلبات قادمة</span>
            <span className="cd-requests__count">{activeRequests.length}</span>
          </div>
          {activeRequests.map(renderRequestCard)}
        </div>
      )}

      {/* Past Requests */}
      <div className="cd-requests">
        <div className="cd-requests__header">
          <span className="cd-requests__title">سجل الزيارات</span>
          {pastRequests.length > 0 && (
            <span className="cd-requests__count">{pastRequests.length}</span>
          )}
        </div>
        {pastRequests.length === 0 && activeRequests.length === 0 ? (
          <div className="cd-requests__empty">
            <i className="bi bi-calendar-x" />
            <span>لا توجد طلبات سابقة</span>
          </div>
        ) : pastRequests.length === 0 ? (
          <div className="cd-requests__empty">
            <i className="bi bi-clock-history" />
            <span>لا توجد زيارات مكتملة بعد</span>
          </div>
        ) : (
          pastRequests.map(renderRequestCard)
        )}
      </div>

      {/* Bottom Sheet */}
      {sheetOpen && (
        <>
          <div className="cd-sheet-overlay" onClick={() => !submitting && !confirmation && setSheetOpen(false)} />
          <div className="cd-sheet">
            <div className="cd-sheet__handle" />

            {confirmation ? (
              <div className="cd-confirmation">
                <div className="cd-confirmation__icon">
                  <i className="bi bi-check-circle-fill" />
                </div>
                <div className="cd-confirmation__title">تم إرسال طلبك</div>
                <div className="cd-confirmation__body">
                  {confirmation.ticketId && (
                    <div className="cd-confirmation__row">
                      <span className="cd-confirmation__label">رقم الطلب</span>
                      <span className="cd-confirmation__value cd-confirmation__value--code">{confirmation.ticketId}</span>
                    </div>
                  )}
                  <div className="cd-confirmation__row">
                    <span className="cd-confirmation__label">التاريخ</span>
                    <span className="cd-confirmation__value">{formatDate(confirmation.requestedDate)}</span>
                  </div>
                  <div className="cd-confirmation__row">
                    <span className="cd-confirmation__label">الموقع</span>
                    <span className="cd-confirmation__value">{confirmation.locationName}</span>
                  </div>
                </div>
                <div className="cd-confirmation__note">سيتم التواصل معك قريباً لتأكيد الموعد</div>
                <button
                  className="cd-sheet__submit"
                  onClick={() => { setSheetOpen(false); setConfirmation(null); }}
                >
                  إغلاق
                </button>
              </div>
            ) : (
            <>
            <div className="cd-sheet__title">طلب نظافة جديد</div>

            {submitError && (
              <div className="cd-sheet__error">
                <i className="bi bi-exclamation-circle" />
                {submitError}
              </div>
            )}

            {/* Location — only show dropdown if multiple */}
            {(data?.locations?.length || 0) > 1 && (
              <div className="cd-sheet__field">
                <label className="cd-sheet__label">الموقع</label>
                <select
                  className="cd-sheet__input touch-input"
                  value={form.locationId}
                  onChange={(e) => handleFormChange('locationId', e.target.value)}
                >
                  <option value="">اختر الموقع...</option>
                  {(data?.locations || []).map(loc => (
                    <option key={loc._id} value={loc._id}>{loc.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Date */}
            <div className="cd-sheet__field">
              <label className="cd-sheet__label">تاريخ النظافة</label>
              <input
                className="cd-sheet__input touch-input"
                type="date"
                value={form.requestedDate}
                onChange={(e) => handleFormChange('requestedDate', e.target.value)}
                min={getTomorrow()}
                dir="ltr"
              />
              {form.requestedDate && (
                <span className="cd-sheet__date-preview">{formatDate(form.requestedDate)}</span>
              )}
            </div>

            {/* Time slots */}
            <div className="cd-sheet__field">
              <label className="cd-sheet__label">الوقت المفضل (اختياري)</label>
              <div className="cd-time-slots">
                {TIME_SLOTS.map(slot => (
                  <button
                    key={slot.value}
                    className={`cd-time-chip ${form.requestedTimeSlot === slot.value ? 'cd-time-chip--active' : ''}`}
                    onClick={() => handleFormChange(
                      'requestedTimeSlot',
                      form.requestedTimeSlot === slot.value ? '' : slot.value
                    )}
                    type="button"
                  >
                    {slot.label}
                  </button>
                ))}
                <button
                  className={`cd-time-chip ${form.requestedTimeSlot && !TIME_SLOTS.some(s => s.value === form.requestedTimeSlot) ? 'cd-time-chip--active' : ''}`}
                  onClick={() => handleFormChange(
                    'requestedTimeSlot',
                    form.requestedTimeSlot && !TIME_SLOTS.some(s => s.value === form.requestedTimeSlot) ? '' : 'custom'
                  )}
                  type="button"
                >
                  أخرى
                </button>
              </div>
              {form.requestedTimeSlot && !TIME_SLOTS.some(s => s.value === form.requestedTimeSlot) && (
                <input
                  type="time"
                  className="cd-sheet__input"
                  style={{ marginTop: 8, maxWidth: 160 }}
                  value={form.requestedTimeSlot === 'custom' ? '' : form.requestedTimeSlot}
                  onChange={(e) => handleFormChange('requestedTimeSlot', e.target.value || 'custom')}
                  dir="ltr"
                />
              )}
            </div>

            {/* Notes */}
            <div className="cd-sheet__field">
              <label className="cd-sheet__label">ملاحظات (اختياري)</label>
              <input
                className="cd-sheet__input touch-input"
                type="text"
                value={form.notes}
                onChange={(e) => handleFormChange('notes', e.target.value)}
                placeholder="تعليمات خاصة..."
              />
            </div>

            <button
              className="cd-sheet__submit"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <span className="cd-layout-loading__spinner" style={{ width: 22, height: 22, borderWidth: 2 }} />
              ) : (
                <>
                  <i className="bi bi-check-lg" />
                  تأكيد
                </>
              )}
            </button>
            </>
            )}
          </div>
        </>
      )}

      {/* Toast */}
      {toast && <div className="cd-toast">{toast}</div>}

      {/* Cancel confirmation dialog */}
      {cancelConfirm && (
        <div className="cd-overlay" onClick={() => !cancelling && setCancelConfirm(null)}>
          <div className="cd-confirm" onClick={e => e.stopPropagation()}>
            <div className="cd-confirm__icon"><i className="bi bi-exclamation-triangle" /></div>
            <div className="cd-confirm__title">تأكيد إلغاء الطلب</div>
            <div className="cd-confirm__body">
              هل تريد إلغاء طلب النظافة بتاريخ {formatDate(cancelConfirm.requestedDate)}
              {cancelConfirm.locationId?.name ? ` — ${cancelConfirm.locationId.name}` : ''}؟
            </div>
            <div className="cd-confirm__actions">
              <button
                className="cd-confirm__btn cd-confirm__btn--danger"
                onClick={handleCancelRequest}
                disabled={cancelling}
              >
                {cancelling ? 'جاري الإلغاء...' : 'نعم، إلغاء الطلب'}
              </button>
              <button
                className="cd-confirm__btn cd-confirm__btn--secondary"
                onClick={() => setCancelConfirm(null)}
                disabled={cancelling}
              >
                تراجع
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Name Dialog */}
      {editNameOpen && (
        <div className="cd-overlay" onClick={() => !editNameSaving && setEditNameOpen(false)}>
          <div className="cd-confirm" onClick={e => e.stopPropagation()}>
            <div className="cd-confirm__title">تعديل الاسم</div>
            <input
              className="cd-sheet__input touch-input"
              type="text"
              value={editNameValue}
              onChange={(e) => setEditNameValue(e.target.value)}
              autoFocus
              dir="auto"
              style={{ marginBottom: 16 }}
            />
            <div className="cd-confirm__actions">
              <button
                className="cd-confirm__btn cd-confirm__btn--primary"
                onClick={handleSaveName}
                disabled={editNameSaving || !editNameValue.trim() || editNameValue.trim().length < 2}
              >
                {editNameSaving ? 'جاري الحفظ...' : 'حفظ'}
              </button>
              <button
                className="cd-confirm__btn cd-confirm__btn--secondary"
                onClick={() => setEditNameOpen(false)}
                disabled={editNameSaving}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
    </ClientLayout>
  );
};

export default ClientDashboard;
